import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CAlert, CSpinner, CStepper } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilBook } from "@coreui/icons";
import type { ApiError } from "../../api/client";
import {
  usePortfolioTracker,
  useSavePortfolioTracker,
  type PortfolioTrackerDto,
} from "../../api/hooks/usePortfolioTracker";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { helpArticlePath, routePaths } from "../../routes/route-paths";
import { MoneyAndDates } from "./components/MoneyAndDates";
import { PickStrategies } from "./components/PickStrategies";
import { TrackerResults } from "./components/TrackerResults";
import { checkDraft, firstDraft, isSaved, savedDraft, toRequest, withStrategies } from "./tracker-setup";

/**
 * What the customer's strategies have made from their signals since they started trading them: pick the
 * strategies, then the money and dates, then the results. The setup is saved, so the page opens on its
 * results next time; the results are worked out afresh on every visit.
 */
export function PortfolioTracker() {
  const { t } = useTranslation(["portfolioTracker", "signals"]);
  const tracker = usePortfolioTracker();

  return (
    <div className="portfolio-tracker">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h1 className="mb-1">{t("page.title")}</h1>
          <p className="text-body-secondary mb-0">{t("page.description")}</p>
        </div>
        <div className="col-12 col-sm-auto d-grid">
          <Link to={helpArticlePath("portfolio-tracker")} className="btn btn-outline-secondary">
            <CIcon icon={cilBook} className="me-2" />
            {t("page.help")}
          </Link>
        </div>
      </div>

      {tracker.isLoading && (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {tracker.isError && <CAlert color="danger">{t("page.loadError")}</CAlert>}

      {tracker.data && tracker.data.strategies.length === 0 && (
        <CAlert color="info">
          {t("page.noStrategies")}{" "}
          <Link to={routePaths.strategies}>{t("signals:empty.browseStrategies")}</Link>
        </CAlert>
      )}

      {tracker.data && tracker.data.strategies.length > 0 && <TrackerSteps tracker={tracker.data} />}
    </div>
  );
}

type Step = 1 | 2 | 3;

function TrackerSteps({ tracker }: { tracker: PortfolioTrackerDto }) {
  const { t } = useTranslation("portfolioTracker");
  const { showWarning } = useToast();
  const { showFailure, clearFailure } = useFailure();
  const save = useSavePortfolioTracker();

  const [step, setStep] = useState<Step>(tracker.setup ? 3 : 1);
  const [initial] = useState(() => (tracker.setup ? savedDraft(tracker, tracker.setup) : firstDraft(tracker)));
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set(initial.picked));
  const [draft, setDraft] = useState(initial.draft);

  const strategies = useMemo(
    () => new Map(tracker.strategies.map((strategy) => [strategy.tradingStrategyId, strategy])),
    [tracker.strategies],
  );
  const names = useMemo(
    () => new Map(tracker.strategies.map((strategy) => [strategy.tradingStrategyId, strategy.name])),
    [tracker.strategies],
  );
  const check = checkDraft(draft, names, tracker.signalsFrom, tracker.asOf);

  // In the order step 1 lists them, which is the order their colours run in.
  const pickedIds = tracker.strategies
    .map((strategy) => strategy.tradingStrategyId)
    .filter((id) => picked.has(id));

  const toTop = () => window.scrollTo({ top: 0 });

  const toMoneyAndDates = () => {
    if (pickedIds.length === 0) return;
    setDraft(withStrategies(draft, pickedIds));
    setStep(2);
  };

  const saveAndShow = async () => {
    if (!check.ok) return;
    const request = toRequest(draft);
    if (isSaved(request, tracker.setup)) {
      setStep(3);
      toTop();
      return;
    }

    clearFailure();
    try {
      await save.mutateAsync(request);
      setStep(3);
      toTop();
    } catch (err) {
      const apiError = err as ApiError;
      const message = err instanceof Error ? err.message : t("setup.saveError");
      if (apiError?.status !== undefined && apiError.status < 500) {
        showWarning(message);
      } else {
        showFailure(message, apiError?.traceId);
      }
    }
  };

  const changeSetup = () => {
    if (!tracker.setup) return;
    const saved = savedDraft(tracker, tracker.setup);
    setPicked(new Set(saved.picked));
    setDraft(saved.draft);
    setStep(2);
    toTop();
  };

  const startAgain = () => {
    const fresh = firstDraft(tracker);
    setPicked(new Set(fresh.picked));
    setDraft(fresh.draft);
    setStep(1);
    toTop();
  };

  // The stepper goes back to any step, forward to the next one only as its own button would.
  const goTo = (next: number) => {
    if (next === 1) setStep(1);
    else if (next === 2) toMoneyAndDates();
    else if (next === 3) void saveAndShow();
  };

  const togglePicked = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <CStepper
        className="mb-3"
        activeStepNumber={step}
        steps={[t("steps.strategies"), t("steps.moneyAndDates"), t("steps.results")]}
        onStepChange={goTo}
      />

      {step === 1 && (
        <PickStrategies
          strategies={tracker.strategies}
          picked={picked}
          onToggle={togglePicked}
          onNext={toMoneyAndDates}
        />
      )}

      {step === 2 && (
        <MoneyAndDates
          strategies={strategies}
          draft={draft}
          onDraftChange={setDraft}
          check={check}
          signalsFrom={tracker.signalsFrom}
          asOf={tracker.asOf}
          saving={save.isPending}
          onBack={() => setStep(1)}
          onSave={saveAndShow}
        />
      )}

      {step === 3 &&
        (tracker.results ? (
          <TrackerResults tracker={tracker} results={tracker.results} onChange={changeSetup} onRestart={startAgain} />
        ) : (
          <CAlert color="info">{t("results.waiting")}</CAlert>
        ))}
    </>
  );
}
